import React, { useContext, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import clsx from 'clsx';
import dayjs from 'dayjs';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import styled from 'styled-components';
import AnalysisSaleContext from 'utils/context/AnalysisSaleContext';
import { decodeData } from 'utils/encodeAndDecodeFunctions';
import { normalizeAnalysisModuleName } from 'utils/normalizeAnalysisModuleName';
import { ReportType, TimeRange } from 'utils/types/AnalysisModels';
import { AnalysisSaleFilterReturnData } from 'utils/types/AnalysisSale';
import { SavedLocationsType } from 'utils/types/V2Locations';

import AnalysisSaleList, { AnalysisElements } from 'components/analysis/AnalysisSaleList';
import FiltersAnalysisSale from 'components/analysis/FiltersAnalysisSale';
import Indicator from 'components/atom/Indicator';
import FiltersContentWrapper from 'components/common/FiltersContentWrapper';
import { PdfFooter, PdfHeader, PdfWrapper } from 'components/common/PdfRenderer/StaticElements';
import V2HeaderSection from 'components/V2HeaderSection';

const Container = styled.div`
    padding: 15px 20px 0;
`;

const PageContainer = styled.div`
    height: 100%;
`;

const AnalysisPage = () => {
    const { analysisSaleState, analysisSaleFiltersState, setLoadingPage, stopLoadingPage, getSavedFilters, handleFillFilterFields, loadAnalysis } = useContext(AnalysisSaleContext);
    const [data, setData] = useState<AnalysisElements>({ areaAnalysisList: [], timeAnalysisList: [], trendResult: [] });
    const [isPrintMode, setIsPrintMode] = useState(false);
    const { chartData, afterSearch } = analysisSaleState;
    const { aiFilterData, filterData } = useParams();

    const decodedAiData = aiFilterData ? decodeData<AnalysisSaleFilterReturnData>(aiFilterData) : null;
    const decodedData = filterData ? decodeData<AnalysisSaleFilterReturnData>(filterData) : null;

    const analysisPrepared = data.areaAnalysisList && data.areaAnalysisList.length > 0 ||
        data.timeAnalysisList && data.timeAnalysisList.length > 0 ||
        data.trendResult && data.trendResult.length > 0;

    const pdfRef = useRef<HTMLDivElement>(null);
    const pdfHeaderRef = useRef<HTMLDivElement>(null);
    const pdfFooterRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (decodedAiData && Object.keys(decodedAiData).length > 0) {
            setLoadingPage();

            const savedLocations = Object.fromEntries(
                decodedAiData.locations
                    .filter((loc) => loc.id)
                    .map((loc) => [
                        loc.id,
                        {
                            id: loc.id,
                            label: loc.fullName || ''
                        }
                    ])
            ) as SavedLocationsType;

            const mappedLocations = decodedAiData.locations
                .filter((loc) => loc.id)
                .map((loc) => ({
                    id: loc.id!,
                    label: loc.fullName || '',
                    children: []
                }));

            const preparedData = {
                ...decodedAiData,
                // @ts-expect-error taki typ przychodzi z backendu
                module: normalizeAnalysisModuleName(decodedAiData.module),
                savedLocations,
                locations: mappedLocations,
                searchLocationResult: decodedAiData.searchLocationResult || []
            };

            handleFillFilterFields(preparedData);
            stopLoadingPage();

            loadAnalysis(preparedData);
            getSavedFilters();
        }
    }, [aiFilterData]);

    useEffect(() => {
        if (decodedData && Object.keys(decodedData).length > 0) {
            setLoadingPage();

            const preparedData = {
                ...decodedData,
                boundaries: '15,35,50,70,100,#',
                timeRange: TimeRange.Tydzien,
                reportType: [ReportType.PrzedzialCzasu, ReportType.PrzedzialPowierzchni]

            };

            handleFillFilterFields(preparedData);
            stopLoadingPage();

            loadAnalysis(preparedData);
            getSavedFilters();
        }
    }, [aiFilterData]);

    const handleDownloadPdf = async () => {
        setIsPrintMode(true);

        setTimeout(async () => {
            const pdfSection = document.getElementById('pdfSection');
            const pdfElement = pdfRef.current;
            const headerElement = pdfHeaderRef.current;
            const footerElement = pdfFooterRef.current;

            if (!pdfSection || !pdfElement || !headerElement || !footerElement) {
                setIsPrintMode(false);

                return;
            }

            try {
                const maxHeight = 1300;
                let spaceLeft = 1300;
                const childrens = Array.from(pdfSection.children);

                childrens.forEach((child, index) => {
                    spaceLeft = spaceLeft - (child as HTMLElement).offsetHeight;

                    if (spaceLeft <= 0) {
                        (childrens[index - 1] as HTMLElement).style.marginBottom = `${spaceLeft + (child as HTMLElement).offsetHeight}px`;
                        spaceLeft = maxHeight - (child as HTMLElement).offsetHeight;
                    }
                });

                const pageHeight = 780;
                const canvasOptions = { scale: 1.5 };

                const [
                    canvas,
                    headerCanvas,
                    footerCanvas
                ] = await Promise.all([
                    html2canvas(pdfElement, canvasOptions),
                    html2canvas(headerElement, canvasOptions),
                    html2canvas(footerElement, canvasOptions)
                ]);

                const dataImage = canvas.toDataURL('image/jpeg', 0.85);
                const dataHeader = headerCanvas.toDataURL('image/jpeg', 0.85);
                const dataFooter = footerCanvas.toDataURL('image/jpeg', 0.85);
                const imgWidth = 595;
                const imgHeight = canvas.height * imgWidth / canvas.width;
                const headerHeight = headerCanvas.height * imgWidth / headerCanvas.width;
                const footerHeight = footerCanvas.height * imgWidth / footerCanvas.width;
                let heightLeft = imgHeight;
                const pdf = new jsPDF('p', 'pt', 'a4');
                let position = 45;
                let page = 1;

                pdf.setFont('helvetica', 'bold');
                pdf.setFontSize(12);
                pdf.setTextColor(3, 13, 69);

                pdf.addImage(dataHeader, 'JPEG', 0, 0, imgWidth, headerHeight);
                pdf.addImage(dataImage, 'JPEG', 0, position, imgWidth, imgHeight);
                pdf.addImage(dataFooter, 'JPEG', 0, 806, imgWidth, footerHeight);
                pdf.text(`0${page}`, 563, 827);
                heightLeft -= pageHeight;

                while (heightLeft >= 0) {
                    position = heightLeft - imgHeight + 43;
                    pdf.addPage();
                    page++;
                    pdf.addImage(dataImage, 'JPEG', 0, position, imgWidth, imgHeight);
                    pdf.addImage(dataHeader, 'JPEG', 0, 0, imgWidth, headerHeight);
                    pdf.addImage(dataFooter, 'JPEG', 0, 806, imgWidth, footerHeight);
                    pdf.text(`0${page}`, 563, 827);
                    heightLeft -= pageHeight;
                }
                pdf.save(`investoro_pl_${dayjs(new Date()).format('DD_MM_YYYY')}.pdf`);
            } finally {
                setIsPrintMode(false);
            }
        }, 2000);
    };

    useEffect(() => {
        if (chartData && chartData.length > 0) {
            const [{ analysisAreaRows: areaAnalysisList, analysisTimeRows: timeAnalysisList, trendResult }] = chartData || {};

            setData({ areaAnalysisList, timeAnalysisList, trendResult });
        }
    }, [chartData]);

    return (
        <PageContainer>
            {isPrintMode &&
                <>
                    <Indicator />
                    <PdfWrapper>
                        <PdfHeader ref={pdfHeaderRef} />
                        <PdfFooter ref={pdfFooterRef} />
                    </PdfWrapper>
                </>
            }
            <V2HeaderSection
                title={'Analiza cen ofertowych'}
                handlePDFClick={analysisPrepared || afterSearch ? handleDownloadPdf : undefined}
            />
            <FiltersContentWrapper className={clsx('offers-list', { active: analysisPrepared || afterSearch })}>
                <Container id="filters">
                    <FiltersAnalysisSale />
                </Container>
                <AnalysisSaleList ref={pdfRef} showList={!!analysisPrepared} analysis={data} isPrintMode={isPrintMode}
                    showNoItemsText={!analysisPrepared && afterSearch} timeRange={analysisSaleFiltersState.timeRange}/>
                {analysisSaleState.isLoading && <Indicator/>}
            </FiltersContentWrapper>
        </PageContainer>
    );
};

export default AnalysisPage;
